/**
 * AuthoritiesDocument — the configurator for an authorities-document line.
 *
 * A document order needs two things from the client: a signed power of attorney,
 * and the property's land-registration data. The backend announces them as ONE
 * configuration type, and they belong to one product, so they are one tab on the
 * configuration step with a page each — the shape the energy certificate already
 * uses for its five pages, not two tabs competing for the same line.
 *
 * This component owns only the page switch. Each page is its own module,
 * untouched: `PowerOfAttorney` and `PropertyInfo` keep their own store keys,
 * their own completeness predicates and their own tests. What ties them together
 * is the Save gate — `selectFlowComplete('power_of_attorney')` requires BOTH, so
 * the line cannot be confirmed with either page blank (see store/selectors).
 *
 * Property information comes first: it is the page with something to fill in.
 * The power of attorney is an optional upload whose predicate answers `true`
 * before anyone has touched it, so OPENING it is what marks it done — which is
 * why every page records its visit in the store.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import type { CartMutationHandler } from '../../adapters/bridge'
import { PfSubTabConfigurator } from '../../components/PfSubTabConfigurator'
import { selectFlowSelfComplete, usePfStore } from '../../store'
import { PfRevampEventName } from '../../analytics/events'
import { dispatchPfRevampEvent } from '../../analytics/usePfRevampAnalytics'
import { PowerOfAttorney } from '../powerOfAttorney'
import { PropertyInfo } from '../propertyInfo'
import { useIsConfigPanelActive } from '../sourceVisuals/configPanelActive'

/** The pages of the document configurator, in the order the client fills them. */
export enum DocumentPage {
  PROPERTY_INFO = 'property_info',
  POWER_OF_ATTORNEY = 'power_of_attorney',
}

export interface AuthoritiesDocumentProps {
  cartItemId: CartItemId
  /** Forwarded to the pages for registry signature uniformity; neither emits. */
  onCartMutation?: CartMutationHandler
}

/**
 * @component AuthoritiesDocument
 * Power of attorney + property information as two pages of one configurator.
 */
export const AuthoritiesDocument: React.FC<AuthoritiesDocumentProps> = ({ cartItemId, onCartMutation }) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'purchase_flow'])

  const [page, setPage] = useState<DocumentPage>(DocumentPage.PROPERTY_INFO)

  const markVisited = usePfStore((s) => s.markConfigPageVisited)
  const visited = usePfStore((s) => s.configPagesVisited[cartItemId])
  const isPanelActive = useIsConfigPanelActive()

  // Opening a page is what records the visit, including the one shown first.
  // The same moment is what analytics counts: this configurator shows two of
  // legacy's steps under one tab, so the tab cannot announce either of them —
  // see MODULE_ANNOUNCED_CONFIG_FLOWS. A hidden tab has not been opened.
  useEffect(() => {
    if (!isPanelActive) return
    markVisited(cartItemId, page)
    dispatchPfRevampEvent({
      type: PfRevampEventName.CONFIG_SCREEN_ENTERED,
      payload: { cartItemId, flowId: page },
    })
  }, [isPanelActive, markVisited, cartItemId, page])

  // Each page badges its OWN data — `selectFlowComplete('power_of_attorney')` is
  // the whole tab (both pages, plus the visit), which is what the tab confirmation and
  // the outer chip read, and would mark this page red for the other page's gap.
  const poaComplete = usePfStore(selectFlowSelfComplete(cartItemId, 'power_of_attorney'))
  const propertyInfoComplete = usePfStore(selectFlowSelfComplete(cartItemId, 'property_info'))

  return (
    <PfSubTabConfigurator
      title={t('purchase_flow:energy_certificate_step.information_fill')}
      description={t('purchase_flow:energy_certificate_step.information_fill_description')}
      activeId={page}
      onSelect={(id) => setPage(id as DocumentPage)}
      // Each page holds unsaved local state (the upload queue, the sheet rows) a switch must not throw away.
      keepMounted
      pages={[
        {
          id: DocumentPage.PROPERTY_INFO,
          label: t(`purchase_flow_revamp:configuration.flow_label.${DocumentPage.PROPERTY_INFO}`),
          visited: !!visited?.[DocumentPage.PROPERTY_INFO],
          isValid: propertyInfoComplete,
          content: <PropertyInfo cartItemId={cartItemId} onCartMutation={onCartMutation} />,
        },
        {
          id: DocumentPage.POWER_OF_ATTORNEY,
          label: t(`purchase_flow_revamp:configuration.flow_label.${DocumentPage.POWER_OF_ATTORNEY}`),
          visited: !!visited?.[DocumentPage.POWER_OF_ATTORNEY],
          isValid: poaComplete,
          content: <PowerOfAttorney cartItemId={cartItemId} onCartMutation={onCartMutation} />,
        },
      ]}
    />
  )
}
